import { useEffect, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSocket } from '@/realtime/socketContext';
import { useCall } from '@/store/call';
import { Avatar } from '@/components/ui';
import { CallIcon, CloseIcon } from '@/components/ui/icons';

const RING_TIMEOUT_MS = 45_000;

export function IncomingCall() {
  const navigate = useNavigate();
  const { socket } = useSocket();
  const incoming = useCall((state) => state.incoming);
  const clear = useCall((state) => state.clear);

  useEffect(() => {
    if (!incoming) return;
    const timer = window.setTimeout(() => {
      socket?.emit('call:decline', { callId: incoming.id });
      clear();
    }, RING_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [clear, incoming, socket]);

  if (!incoming) return null;

  function accept() {
    if (!incoming) return;
    socket?.emit('call:accept', { callId: incoming.id });
    clear();
    navigate(`/call/${incoming.id}`);
  }

  function decline() {
    if (!incoming) return;
    socket?.emit('call:decline', { callId: incoming.id });
    clear();
  }

  return (
    <div
      className="fixed inset-0 z-[80] grid place-items-center bg-black/70 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="incoming-call-title"
    >
      <section className="w-full max-w-sm rounded-2xl bg-surface-1 p-6 text-center shadow-2xl ring-1 ring-surface-4">
        <div className="mx-auto w-fit rounded-full ring-4 ring-emerald-400/20">
          <Avatar user={incoming.startedBy} size="xl" />
        </div>
        <h2 id="incoming-call-title" className="mt-5 text-xl font-semibold text-ink">
          {incoming.startedBy.name}
        </h2>
        <p className="mt-1 text-sm text-slate-400">Calling in {incoming.channelName}</p>
        <div className="mt-7 flex justify-center gap-10">
          <CallAction label="Decline" tone="danger" onClick={decline}>
            <CloseIcon size="lg" />
          </CallAction>
          <CallAction label="Accept" tone="accept" onClick={accept}>
            <CallIcon size="lg" />
          </CallAction>
        </div>
      </section>
    </div>
  );
}

function CallAction({
  label,
  tone,
  onClick,
  children,
}: {
  label: string;
  tone: 'danger' | 'accept';
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-2">
      <button
        type="button"
        onClick={onClick}
        aria-label={label}
        className={`grid h-14 w-14 place-items-center rounded-full text-white transition-transform duration-150 hover:scale-105 active:scale-95 ${
          tone === 'accept' ? 'bg-emerald-500' : 'bg-red-500'
        }`}
      >
        {children}
      </button>
      <span className="text-xs text-slate-400">{label}</span>
    </div>
  );
}
